import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import {
  cancelKnowledgeVisualEnrichmentJob,
  getKnowledgeVisualEnrichmentJob,
  preflightKnowledgeVisualEnrichment,
  retryKnowledgeVisualEnrichmentJob,
  startKnowledgeVisualEnrichmentJob,
} from './api';
import type {
  KnowledgeVisualEnrichmentJob,
  KnowledgeVisualEnrichmentPreflight,
} from './types';

export const OPEN_ATHENA_VISUAL_ENRICHMENT_EVENT = 'open-athena-visual-enrichment';

export const AthenaVisualProfileIcon: React.FC<{ fontSize?: 'small' | 'medium' | 'large' }> = ({
  fontSize = 'small',
}) => <AutoAwesomeIcon fontSize={fontSize} />;

function errorMessage(error: any): string {
  return error?.response?.data?.message || error?.message || 'Ett oväntat fel inträffade.';
}

function isTerminal(status?: string): boolean {
  return ['completed', 'partial', 'failed', 'cancelled'].includes(String(status || ''));
}

function displayJobError(value: unknown): string {
  const message = String(value || '').trim();
  const normalized = message.toLocaleLowerCase('sv-SE');
  if (normalized.includes('temperature') && normalized.includes('deprecated')) {
    return 'Claude-modellen avvisade en utfasad parameter. Starta om backend och välj Försök igen.';
  }
  return message;
}

const VisualEnrichmentBulkAction: React.FC<{ selectedAssetIds: string[] }> = ({ selectedAssetIds }) => {
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [preflight, setPreflight] = React.useState<KnowledgeVisualEnrichmentPreflight | null>(null);
  const [job, setJob] = React.useState<KnowledgeVisualEnrichmentJob | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const selectedRef = React.useRef<string[]>(selectedAssetIds);

  React.useEffect(() => {
    selectedRef.current = selectedAssetIds;
  }, [selectedAssetIds]);

  React.useEffect(() => {
    const handleOpen = async () => {
      const ids = [...selectedRef.current];
      setOpen(true);
      setLoading(true);
      setError(null);
      setPreflight(null);
      setJob(null);
      try {
        setPreflight(await preflightKnowledgeVisualEnrichment(ids));
      } catch (requestError) {
        setError(errorMessage(requestError));
      } finally {
        setLoading(false);
      }
    };
    window.addEventListener(OPEN_ATHENA_VISUAL_ENRICHMENT_EVENT, handleOpen);
    return () => window.removeEventListener(OPEN_ATHENA_VISUAL_ENRICHMENT_EVENT, handleOpen);
  }, []);

  React.useEffect(() => {
    if (!job || isTerminal(job.status)) return;
    const timer = window.setInterval(async () => {
      try {
        setJob(await getKnowledgeVisualEnrichmentJob(job.id));
      } catch (requestError) {
        setError(errorMessage(requestError));
      }
    }, 1500);
    return () => window.clearInterval(timer);
  }, [job]);

  const startJob = async (force = false) => {
    setLoading(true);
    setError(null);
    try {
      setJob(await startKnowledgeVisualEnrichmentJob(selectedRef.current, force));
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setLoading(false);
    }
  };

  const cancelJob = async () => {
    if (!job) return;
    setLoading(true);
    try {
      setJob(await cancelKnowledgeVisualEnrichmentJob(job.id));
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setLoading(false);
    }
  };

  const retryFailed = async () => {
    if (!job) return;
    setLoading(true);
    setError(null);
    try {
      setJob(await retryKnowledgeVisualEnrichmentJob(job.id));
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setLoading(false);
    }
  };

  const completed = job ? job.summary.searchable + job.summary.alreadyCurrent + job.summary.unsupported
    + job.summary.missing + job.summary.failed + job.summary.cancelled + job.summary.stale : 0;
  const progress = job?.summary.total ? Math.round((completed / job.summary.total) * 100) : 0;
  const totalTokens = job ? job.usage.inputTokens + job.usage.outputTokens
    + Number(job.usage.cacheCreationInputTokens || 0) + Number(job.usage.cacheReadInputTokens || 0) : 0;
  const jobErrors = Array.from(new Set(
    (job?.items || []).map((item) => displayJobError(item.error)).filter(Boolean),
  )).slice(0, 4);
  const canCreateNew = !!preflight && preflight.eligible > 0;
  const canRecreateCurrent = !!preflight && preflight.alreadyCurrent > 0;
  const allSelectedAreCurrent = !!preflight && preflight.total > 0 && preflight.alreadyCurrent === preflight.total;

  return (
    <Dialog open={open} onClose={() => isTerminal(job?.status) || !job ? setOpen(false) : undefined} fullWidth maxWidth="sm">
      <DialogTitle>Skapa Athena-bildprofil</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Bildprofilen lagras endast i Knowledge Hub och används av Chatta med Athena. Befintlig AI-beskrivning och assetmetadata ändras inte.
          </Typography>
          {loading && !job && <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={28} /></Box>}
          {error && <Alert severity="error">{error}</Alert>}
          {preflight && !job && (
            <>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip label={`${preflight.total} valda`} />
                <Chip color="primary" label={`${preflight.eligible} kan profileras`} />
                <Chip label={`${preflight.alreadyCurrent} redan aktuella`} />
                {preflight.unsupported > 0 && <Chip color="warning" label={`${preflight.unsupported} stöds inte`} />}
                {preflight.missing > 0 && <Chip color="error" label={`${preflight.missing} saknas`} />}
              </Stack>
              {preflight.eligible > 0 && preflight.alreadyCurrent > 0 && (
                <Alert severity="info">
                  Starta skapar profiler för {preflight.eligible} bilder och skippar automatiskt {preflight.alreadyCurrent} som redan är aktuella.
                </Alert>
              )}
              {preflight.eligible > 0 && preflight.costEstimate && (
                <Alert severity="info">
                  Uppskattning baserat på senaste körningar: ca ${preflight.costEstimate.estimatedCostPerImageUsd.toFixed(3)}/bild · ca ${preflight.costEstimate.estimatedBatchCostUsd.toFixed(2)} för {preflight.eligible} bilder.
                </Alert>
              )}
              {allSelectedAreCurrent && (
                <Alert severity="success">
                  Alla valda bilder har redan en aktuell Athena-bildprofil. Du kan stänga dialogen eller skapa om profilerna om du vill analysera bilderna på nytt.
                </Alert>
              )}
              {preflight.items.some((item) => item.reason) && (
                <Alert severity="info">
                  {preflight.items.filter((item) => item.reason).slice(0, 3).map((item) => item.reason).join(' • ')}
                </Alert>
              )}
            </>
          )}
          {job && (
            <>
              <Box>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                  <Typography variant="body2" fontWeight={700}>{job.status}</Typography>
                  <Typography variant="body2">{progress}%</Typography>
                </Stack>
                <LinearProgress variant="determinate" value={progress} />
              </Box>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip color="success" label={`${job.summary.searchable} sökbara`} />
                <Chip label={`${job.summary.alreadyCurrent} redan aktuella`} />
                <Chip color="primary" label={`${job.summary.processing} bearbetas`} />
                <Chip label={`${job.summary.queued} köade`} />
                {job.summary.failed > 0 && <Chip color="error" label={`${job.summary.failed} misslyckades`} />}
                {job.summary.stale > 0 && <Chip color="warning" label={`${job.summary.stale} ändrades under körning`} />}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Claude: {totalTokens.toLocaleString('sv-SE')} tokens · faktisk beräknad kostnad ca ${job.usage.estimatedCostUsd.toFixed(2)}
                {job.usage.estimatedCostPerImageUsd ? ` · ca $${job.usage.estimatedCostPerImageUsd.toFixed(3)}/bild` : ''}
              </Typography>
              {jobErrors.length > 0 && (
                <Alert severity="warning">
                  {jobErrors.join(' • ')}
                </Alert>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {job && !isTerminal(job.status) && <Button onClick={cancelJob} disabled={loading}>Avbryt jobb</Button>}
        {job && isTerminal(job.status) && (job.summary.failed > 0 || job.summary.stale > 0) && (
          <Button onClick={retryFailed} disabled={loading}>Försök igen</Button>
        )}
        {!job && <Button onClick={() => setOpen(false)}>Stäng</Button>}
        {!job && canRecreateCurrent && (
          <Button onClick={() => startJob(true)} disabled={loading}>
            Skapa om profiler
          </Button>
        )}
        {!job && canCreateNew && (
          <Button variant="contained" onClick={() => startJob(false)} disabled={loading}>
            Skapa profiler
          </Button>
        )}
        {job && isTerminal(job.status) && <Button variant="contained" onClick={() => setOpen(false)}>Klar</Button>}
      </DialogActions>
    </Dialog>
  );
};

export default VisualEnrichmentBulkAction;
